import type { Canvas } from "fabric";
import { type MutableRefObject, useEffect } from "react";
import { useAppQuery } from "../../../../../http/useAppQuery.ts";
import { DEFAULT_IMAGE_SIZE } from "../../../../constants.ts";
import {
  IMAGE_RESOURCE_TYPE,
  VIDEO_RESOURCE_TYPE,
} from "../../../constants.ts";
import type { DetailedAsset, DetailedContent } from "../../../types.ts";
import { overlayDisplayedContentUuidStore } from "../stores/overlayDisplayedContentUuidStore.ts";
import { designerCanvasStore } from "./stores/designerCanvasStore.ts";
import type { AssetLayer } from "./types.ts";
import { buildImageLayer } from "./utils/image.ts";
import { buildVideoLayer } from "./utils/video.ts";

// We pass the content here and not the asset because of overlay content.
// FIXME: assetLayerBuilder will be at some point renamed to AssetLayerBuilder, when we will have devised a way to represent overlay content directly in the asset attributes.
export const AssetLayerBuilder = ({
  layer,
  videoRef,
  fabricCanvas,
}: {
  layer: AssetLayer;
  videoRef?: MutableRefObject<HTMLVideoElement | null>;
  fabricCanvas?: Canvas;
}) => {
  const { left, top } = layer;
  const { overlayDisplayedContentUuid } =
    overlayDisplayedContentUuidStore.useState();

  const { data: contentData } = useAppQuery<DetailedContent>({
    queryKey: `contents/${overlayDisplayedContentUuid ?? ""}`,
    enabled: !!overlayDisplayedContentUuid,
  });

  const { canvasSize } = designerCanvasStore.useState();

  const {
    assetUuid,
    targetWidth: layerTargetWidth,
    targetHeight: layerTargetHeight,
  } = layer;

  const { data: assetData } = useAppQuery<DetailedAsset>({
    queryKey: `assets/${assetUuid}`,
  });

  const assetContentWidth = assetData?.content.width ?? DEFAULT_IMAGE_SIZE;
  const assetContentHeight = assetData?.content.height ?? DEFAULT_IMAGE_SIZE;

  const ratioAssetContent = assetContentHeight
    ? assetContentWidth / assetContentHeight
    : undefined;
  const ratioCurrentLayer = layerTargetHeight
    ? layerTargetWidth / layerTargetHeight
    : undefined;

  const content = overlayDisplayedContentUuid
    ? contentData
    : assetData?.content;

  // This hook is used to update the targetWidth and targetHeight following an assetData?.content.uuid change.
  useEffect(() => {
    if (
      !ratioAssetContent ||
      !ratioCurrentLayer ||
      Math.abs(ratioAssetContent - ratioCurrentLayer) > 0.01
    ) {
      // FIXME: For Beta users, the canvasSize is updated only when the ratio of the assetContentWidth and assetContentHeight is different from the ratio of the layerTargetWidth and layerTargetHeight.
      // This is an UX choice: for example when switching from an upscaled image to a downscaled one, we don't want to change the canvasSize while if the user select an expanded image, we want
      // to change the targetWidth and targetHeight to have an image that is not stretched.
      // The correct comparison should be on "aspectRation", but this would mean storing the aspectRatio in the layer attributes and adding an aspectRatio attribute to Content.
      layer.set({
        targetWidth: assetContentWidth,
        targetHeight: assetContentHeight,
      });
    }
  }, [
    assetContentWidth,
    assetContentHeight,
    canvasSize,
    left,
    top,
    layer,
    ratioAssetContent,
    ratioCurrentLayer,
  ]);

  useEffect(() => {
    if (fabricCanvas && content?.url && layerTargetWidth && layerTargetHeight) {
      switch (content.resource_type) {
        case IMAGE_RESOURCE_TYPE:
          void buildImageLayer({
            imageLayer: layer,
            targetWidth: layerTargetWidth,
            targetHeight: layerTargetHeight,
            imageUrl: content.url,
          });
          designerCanvasStore.setIsVideoDisplayed(false);
          designerCanvasStore.setIsVideoPlaying(false);
          if (videoRef) videoRef.current = null;
          fabricCanvas.renderAll();
          break;
        case VIDEO_RESOURCE_TYPE:
          // FIXME: The interface should be similar to buildImageLayer, except that we need to pass the
          // the videoRef and the onLoadedData callback. To be updated when we have a proper way to store the video element.
          buildVideoLayer({
            assetLayer: layer,
            videoUrl: content.url,
            videoWidth: content.width ?? DEFAULT_IMAGE_SIZE,
            videoHeight: content.height ?? DEFAULT_IMAGE_SIZE,
            targetWidth: layerTargetWidth,
            targetHeight: layerTargetHeight,
            videoRef,
            onLoadedData: () => {
              designerCanvasStore.setIsVideoDisplayed(true);
              designerCanvasStore.setIsVideoPlaying(false);
              fabricCanvas.renderAll();
            },
          });
          break;
      }
    }
  }, [
    content,
    layer,
    fabricCanvas,
    videoRef,
    layerTargetWidth,
    layerTargetHeight,
  ]);

  // XXX: We don't need to render anything here, we just need to build the image layer that will be rendered by the fabric canvas.
  // As we cannot define hooks conditionally, we are forced to define per image layer (hence contentUuid) a hook that will get the image data and build the image layer.
  // FIXME: The day our backend is able to query several contents at once we will be able to build the different image layers in a single query and a use effect.
  // This would remove the need to define a hook per image layer and having empty components.
  return null;
};
